import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
  Paper,
  Avatar,
  IconButton,
  Grid,
} from "@mui/material";
import {
  Close as CloseIcon,
  Person as PersonIcon,
  SmartToy as BotIcon,
  AccessTime as TimeIcon,
  Message as MessageIcon,
} from "@mui/icons-material";
import { useSuperChatLogStore } from "@/store/useSuperChatLogStore";

/**
 * SessionDetail 템플릿 Props
 */
export type SessionDetailProps = {
  open: boolean;
  session: any;
  onClose: () => void;
  isModal?: boolean; // 모달인지 페이지인지 구분
};

/**
 * 채팅 세션 상세 보기 템플릿
 *
 * @description 개별 세션의 전체 대화 내용을 표시하는 모달 컴포넌트
 * @param props SessionDetailProps
 * @returns JSX.Element
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-20
 */
export const SessionDetail = (props: SessionDetailProps) => {
  const { open, session, onClose, isModal = true } = props;
  const { messages, loading } = useSuperChatLogStore();

  if (!session) return null;

  // 세션 상태 색상
  const getStatusColor = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "success";
      case "CLOSED":
        return "default";
      case "PAUSED":
        return "warning";
      default:
        return "default";
    }
  };

  // 세션 상태 텍스트
  const getStatusText = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "활성";
      case "CLOSED":
        return "종료";
      case "PAUSED":
        return "일시정지";
      default:
        return status;
    }
  };

  // 카테고리 텍스트
  const getCategoryText = (category: string) => {
    switch (category) {
      case "GENERAL":
        return "일반문의";
      case "TECHNICAL":
        return "기술지원";
      case "BILLING":
        return "요금문의";
      case "SERVICE":
        return "서비스소개";
      default:
        return category || "기타";
    }
  };

  // 모달이 아닌 경우 (페이지에서 사용) 다른 레이아웃 사용
  if (!isModal) {
    return (
      <Box sx={{ p: 0 }}>
        {/* 세션 기본 정보 */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            기본 정보
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  세션 ID
                </Typography>
                <Typography variant="body1" fontFamily="monospace">
                  {session.sessionUuid}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  제목
                </Typography>
                <Typography variant="body1">{session.sessionTitle}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  상태
                </Typography>
                <Chip
                  label={getStatusText(session.sessionStatus)}
                  size="small"
                  color={getStatusColor(session.sessionStatus) as any}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  카테고리
                </Typography>
                <Chip
                  label={getCategoryText(session.primaryCategory)}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  시작 시간
                </Typography>
                <Typography variant="body1">
                  {new Date(session.startedAt).toLocaleString("ko-KR")}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  마지막 활동
                </Typography>
                <Typography variant="body1">
                  {new Date(session.lastActivityAt).toLocaleString("ko-KR")}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* 사용자 정보 */}
          {session.user && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                사용자 정보
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar>
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography variant="body1">{session.user.name}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {session.user.email}
                  </Typography>
                  {session.user.phone && (
                    <Typography variant="body2" color="textSecondary">
                      {session.user.phone}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </Paper>

        {/* 대화 내용 */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            대화 내용 ({messages.length}개 메시지)
          </Typography>

          {loading ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography color="textSecondary">
                메시지를 불러오는 중...
              </Typography>
            </Box>
          ) : messages.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography color="textSecondary">메시지가 없습니다.</Typography>
            </Box>
          ) : (
            <Box>
              {messages.map((message, index) => (
                <Box key={message.messageId} sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                      flexDirection:
                        message.messageType === "USER" ? "row-reverse" : "row",
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor:
                          message.messageType === "USER"
                            ? "primary.main"
                            : "grey.400",
                        width: 32,
                        height: 32,
                      }}
                    >
                      {message.messageType === "USER" ? (
                        <PersonIcon fontSize="small" />
                      ) : (
                        <BotIcon fontSize="small" />
                      )}
                    </Avatar>

                    <Paper
                      sx={{
                        p: 2,
                        maxWidth: "70%",
                        bgcolor:
                          message.messageType === "USER"
                            ? "primary.50"
                            : "grey.50",
                      }}
                    >
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {message.messageContent}
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mt: 1,
                        }}
                      >
                        <TimeIcon fontSize="small" color="disabled" />
                        <Typography variant="caption" color="textSecondary">
                          {new Date(message.regDttm).toLocaleString("ko-KR")}
                        </Typography>

                        {message.messageType === "BOT" && message.aiModelUsed && (
                          <>
                            <Divider orientation="vertical" flexItem />
                            <Typography variant="caption" color="textSecondary">
                              {message.aiModelUsed}
                            </Typography>
                          </>
                        )}

                        {message.messageType === "BOT" &&
                          message.processingTimeMs > 0 && (
                            <>
                              <Divider orientation="vertical" flexItem />
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                {message.processingTimeMs}ms
                              </Typography>
                            </>
                          )}
                      </Box>

                      {message.userRating && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="textSecondary">
                            평가: {message.userRating}
                          </Typography>
                          {message.feedbackText && (
                            <Typography variant="caption" display="block">
                              피드백: {message.feedbackText}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Paper>
                  </Box>

                  {index < messages.length - 1 && (
                    <Divider sx={{ my: 2, opacity: 0.3 }} />
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      </Box>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">세션 상세 정보</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* 세션 기본 정보 */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            기본 정보
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  세션 ID
                </Typography>
                <Typography variant="body1" fontFamily="monospace">
                  {session.sessionUuid}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  제목
                </Typography>
                <Typography variant="body1">{session.sessionTitle}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  상태
                </Typography>
                <Chip
                  label={getStatusText(session.sessionStatus)}
                  size="small"
                  color={getStatusColor(session.sessionStatus) as any}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  카테고리
                </Typography>
                <Chip
                  label={getCategoryText(session.primaryCategory)}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  시작 시간
                </Typography>
                <Typography variant="body1">
                  {new Date(session.startedAt).toLocaleString("ko-KR")}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  마지막 활동
                </Typography>
                <Typography variant="body1">
                  {new Date(session.lastActivityAt).toLocaleString("ko-KR")}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* 사용자 정보 */}
          {session.user && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                사용자 정보
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar>
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography variant="body1">{session.user.name}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {session.user.email}
                  </Typography>
                  {session.user.phone && (
                    <Typography variant="body2" color="textSecondary">
                      {session.user.phone}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </Paper>

        {/* 대화 내용 */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            대화 내용 ({messages.length}개 메시지)
          </Typography>

          {loading ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography color="textSecondary">
                메시지를 불러오는 중...
              </Typography>
            </Box>
          ) : messages.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography color="textSecondary">메시지가 없습니다.</Typography>
            </Box>
          ) : (
            <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
              {messages.map((message, index) => (
                <Box key={message.messageId} sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                      flexDirection:
                        message.messageType === "USER" ? "row-reverse" : "row",
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor:
                          message.messageType === "USER"
                            ? "primary.main"
                            : "grey.400",
                        width: 32,
                        height: 32,
                      }}
                    >
                      {message.messageType === "USER" ? (
                        <PersonIcon fontSize="small" />
                      ) : (
                        <BotIcon fontSize="small" />
                      )}
                    </Avatar>

                    <Paper
                      sx={{
                        p: 2,
                        maxWidth: "70%",
                        bgcolor:
                          message.messageType === "USER"
                            ? "primary.50"
                            : "grey.50",
                      }}
                    >
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {message.messageContent}
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mt: 1,
                        }}
                      >
                        <TimeIcon fontSize="small" color="disabled" />
                        <Typography variant="caption" color="textSecondary">
                          {new Date(message.regDttm).toLocaleString("ko-KR")}
                        </Typography>

                        {message.messageType === "BOT" && message.aiModelUsed && (
                          <>
                            <Divider orientation="vertical" flexItem />
                            <Typography variant="caption" color="textSecondary">
                              {message.aiModelUsed}
                            </Typography>
                          </>
                        )}

                        {message.messageType === "BOT" &&
                          message.processingTimeMs > 0 && (
                            <>
                              <Divider orientation="vertical" flexItem />
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                {message.processingTimeMs}ms
                              </Typography>
                            </>
                          )}
                      </Box>

                      {message.userRating && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="textSecondary">
                            평가: {message.userRating}
                          </Typography>
                          {message.feedbackText && (
                            <Typography variant="caption" display="block">
                              피드백: {message.feedbackText}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Paper>
                  </Box>

                  {index < messages.length - 1 && (
                    <Divider sx={{ my: 2, opacity: 0.3 }} />
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
};
